import React, {useContext, useState} from 'react';
import {createUseStyles} from 'react-jss';
import gql from 'graphql-tag';
import {GraphQLError} from 'graphql';
import {useMutation} from '@apollo/client';
import cn from 'classnames';
import Tip, {TipType} from '../../../ui/Tip';
import UILink from '../../../ui/UILink';
import {showError, showInfo} from '../../../ui/ToastNotification';
import Modal from '../../../ui/modal/Modal';
import ModalContent from '../../../ui/modal/ModalContent';
import ModalButtons from '../../../ui/modal/ModalButtons';
import PrimaryButton from '../../../ui/PrimaryButton';
import TextButton from '../../../ui/TextButton';
import OldUrls from '../../../models/utils/OldUrls';
import logAction from '../../../utils/Logging';
import Slide1 from '../../../assets/notifications/dyk-your-account/slide-1.png';
import Slide2 from '../../../assets/notifications/dyk-your-account/slide-2.png';
import Slide3 from '../../../assets/notifications/dyk-your-account/slide-3.png';
import WowGif from '../../../assets/notifications/dyk-your-account/wow.gif';
import {AccountContext} from '../../../utils/Context';
import Carousel from '../../../ui/Carousel';

export default function DoYouKnowYourAccount() {
  const category = 'dyk-your-account';
  const [dismiss] = useMutation(mutation);
  const [showModal, setShowModal] = useState(false);
  const [mainContent, setMainContent] = useState(true);

  const styles = createUseStyles({
    gif: {
      backgroundImage: `url('${Slide3}')`,
    },
  })();

  const account = useContext(AccountContext);

  const onDismiss = React.useCallback(() => {
    dismiss({
      variables: {
        id: category,
      },
    }).then(
      () => {
        showInfo('Notification dismissed');
      },
      (apolloError) => {
        apolloError.graphQLErrors.map((error: GraphQLError) => showError(error.message));
      },
    );
  }, [dismiss]);

  const onLearnMore = () => {
    logAction(category, 'learn-more', account?.id || 0);
    setShowModal(true);
  };

  const onDismissNotification = () => {
    logAction(category, 'dismiss', account?.id || 0);
    onDismiss();
  };

  const onClose = () => {
    setShowModal(false);
    setMainContent(true);
    if (!mainContent) {
      onDismiss();
    }
  };

  const onYesNo = (value: 'yes' | 'no') => {
    logAction(category, `new-${value}`, account?.id || 0);
    setMainContent(false);
    onDismiss();
  };

  const onStart = () => {
    logAction(category, 'start', account?.id || 0);
    window.location.assign(OldUrls.listOfCategories());
  };

  const onNotInterested = () => {
    logAction(category, 'not-interested', account?.id || 0);
    setMainContent(false);
  };

  const onAskLater = () => {
    logAction(category, 'ask-later', account?.id || 0);
    setShowModal(false);
  };

  const modalTitle = mainContent ? 'Your workshops in one place' : 'One more thing!';

  return (
    <>
      <Modal title={modalTitle} show={showModal} onClose={onClose}>
        <ModalContent>
          {mainContent &&
          <>
            <p className="mb-3">
              Did you get your Workshop Butler account by becoming a licensed trainer/facilitator or collaborating with
              a
              training company? It does not matter.
            </p>
            <p className="mb-3 text-center">
              <b>The account belongs to you.</b>
            </p>
            <p>
              It means that you can:
            </p>
            <Carousel>
              <div className="rounded-md flex flex-col mt-3">
                <span className="bg-yellow-light p-2">
                Manage all workshops, not only the licensed ones, right here, at Workshop Butler.
                </span>
                <span className="inline-block relative">
                  <img src={Slide1} alt="Manage all workshops"/>
                </span>
              </div>
              <div className="rounded-md flex flex-col mt-3">
                <span className="bg-yellow-light p-2">
                Integrate your website with WSB, and show only your events on it, accept registrations and payments.
                </span>
                <span className="inline-block relative">
                  <img src={Slide2} alt="Integrate with your website"/>
                </span>
              </div>
              <div className="rounded-md flex flex-col mt-3">
                <span className="bg-yellow-light p-2">
                Use the power of WSB for all workshops for free.<br/>
                  <i className="text-sm text-gray-dark">Free plan includes one paid non-licensed event per month; licensed events are not limited.</i>
                </span>
                <span className={cn('inline-block relative justify-center flex bg-cover', styles.gif)}>
                  <img
                    src={WowGif}
                    alt="Free for all"/>
                </span>
              </div>
            </Carousel>
            <p className="mt-1">
              Are you ready to start? It&apos;s easy: add a new category, and create an event under it.
            </p>
          </>
          }
          {!mainContent &&
          <>
            <p>
              We would like to know better how well our users are familiar with existing functionality.
            </p>
            <br/>
            <p>
              Tell us, please, if the information about the WSB account was new to you.
            </p>
          </>
          }
        </ModalContent>
        <ModalButtons centered={mainContent}>
          {mainContent &&
          <>
            <PrimaryButton onClick={onStart}>Let&apos;s begin</PrimaryButton>
            <TextButton onClick={onAskLater}>Later, I&apos;m busy</TextButton>
            <TextButton onClick={onNotInterested}>Not interested</TextButton>
          </>
          }
          {!mainContent &&
          <>
            <TextButton onClick={() => onYesNo('yes')}>Yes, it was new to me</TextButton>
            <TextButton onClick={() => onYesNo('no')}>No, I already knew that</TextButton>
          </>
          }
        </ModalButtons>
      </Modal>
      <Tip type={TipType.Info} className="mb-4">
        <div className="flex flex-col">
          <div className="pb-3 font-bold">
            Do you know that...
          </div>
          <div className="pb-3">
            you can use your Workshop Butler account to manage <b>ALL</b> your events and workshops?
          </div>
          <div className="flex items-center">
            <UILink href="#" onClick={onLearnMore} className="not-italic">Learn More</UILink>
            <UILink href="#" onClick={onDismissNotification} className="ml-6 not-italic">Dismiss</UILink>
          </div>
        </div>
      </Tip>
    </>
  );
}

const mutation = gql`
  mutation DismissNotification($id: ID!) {
    dismissDashboardNotification(id: $id) {
      id
      visible
    }
  }
`;
